import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  Radio,
  CheckCircle2,
  Clock,
  Sparkles,
  ExternalLink,
  Square,
  Layers,
  FileText,
  Code2,
  HelpCircle,
  BookOpen,
  ChevronDown,
  ChevronRight,
  PlayCircle,
  Users,
  Download,
  ClipboardCheck,
  Plus,
  Package,
  RotateCcw,
  Trash2,
  Lock,
  Unlock,
  CalendarClock,
} from "lucide-react";
import { AppButton, ConfirmModal, AppModal } from "../../ui";
import {
  adminListSessions,
  endSmartSession,
  restartSmartSession,
  setSessionResourcesUnlock,
  fetchSessionRecordings,
  deleteSessionRecording,
  fetchTrainerSessionDetail,
  addTrainerSessionResource,
} from "../../../utils/smartLearning";
import useMaterialViewer from "../../Materials/useMaterialViewer";
import ResourcePicker from "../../LearningResources/ResourcePicker";
import { KIND_META } from "../../LearningResources/resourceKinds";
import ScormPlayer from "../../SelfPaced/ScormPlayer";
import SessionEvaluationModal from "./SessionEvaluationModal";
import Shimmer from "../../Common/Shimmer";
import apiClient from "../../../utils/apiClient";
import SessionDateLine from "../schedule/SessionDateLine";
import ScheduleNotice, { scheduleNeedsNotice } from "../schedule/ScheduleNotice";
import RescheduleDialog from "../schedule/RescheduleDialog";

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-26 (Phase 5); trainerSmartSessions.scss deleted.
//
// 🔴 THE PALETTE: four Sass variables at the top of the sheet — `$tss-accent:
// #7c3aed`, `$tss-accent-soft`, `$tss-live: #ef4444`, `$tss-emerald: #10b981` —
// used at about forty call sites. A private brand accent on a trainer-facing
// screen, which is exactly what `applyBrandTokens` exists to make impossible.
// They are `--primary`, `--danger` and `--success` now.
//
// ⚠️ ~120 lines were DEAD: `.tss-modal-body`, `.tss-modal-sub`,
// `.tss-modal-error`, `.tss-label`, `.tss-hint`, `.tss-mode` and
// `.tss-auto-note` — a whole "start class modal" that this component no longer
// renders and nothing else references. Deleted rather than converted.
//
// The five resource-kind colours were four blues and an amber (`#7c3aed`
// violet, `#6366f1` indigo, `#2563eb` blue, `#0891b2` cyan, `#d97706` amber),
// so quiz/scorm and document/coding were near-indistinguishable pairs. They are
// five different hues off the `--chart-*` ramp now, which is what that ramp is
// for — the point of the colour is to tell five kinds apart.
//
// `tss-pulse-dot` was `live-pulse` in the config to the frame; reused, with the
// `prefers-reduced-motion` opt-out as `motion-reduce:tw-animate-none`.

const ROOT = "tw-flex tw-flex-col tw-gap-5";

const ERROR =
  "tw-rounded tw-border tw-border-solid tw-border-danger/20 tw-bg-danger/[0.08] " +
  "tw-px-4 tw-py-3 tw-text-[13px] tw-text-danger-fg";
const ERROR_INLINE = ERROR + " tw-mb-2";

const EMPTY =
  "tw-rounded-lg tw-border tw-border-dashed tw-border-line tw-bg-surface-raised " +
  "tw-px-5 tw-py-12 tw-text-center";
const EMPTY_ICON =
  "tw-mb-3 tw-inline-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center " +
  "tw-rounded-[14px] tw-bg-primary/10 tw-text-primary";
const EMPTY_TITLE = "tw-mb-1 tw-mt-0 tw-text-base tw-font-semibold tw-text-content";
const EMPTY_DESC =
  "tw-mx-auto tw-my-0 tw-max-w-[460px] tw-text-[13px] tw-leading-[1.5] tw-text-content-subtle";

// ── Module group ──
const MODULE =
  "tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-line tw-bg-surface-raised";
const MODULE_HEAD =
  "tw-flex tw-items-center tw-gap-2.5 tw-border-0 tw-border-b tw-border-solid tw-border-line " +
  "tw-px-[1.125rem] tw-py-3.5";
const MODULE_ICON =
  "tw-inline-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-sm " +
  "tw-bg-primary/10 tw-text-primary";
const MODULE_TITLE =
  "tw-m-0 tw-min-w-0 tw-flex-1 tw-text-[15px] tw-font-semibold tw-text-content";
const MODULE_COUNT = "tw-text-xs tw-text-content-subtle";
const MODULE_EMPTY =
  "tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-px-[1.125rem] " +
  "tw-py-4 tw-text-[13px] tw-text-content-subtle";

// ── Module topics / subtopics (read-only, from the syllabus template) ──
const TOPICS =
  "tw-flex tw-flex-col tw-gap-2.5 tw-border-0 tw-border-b tw-border-solid tw-border-line " +
  "tw-bg-surface-alt tw-px-[1.125rem] tw-py-3";
const TOPIC_TITLE =
  "tw-flex tw-items-center tw-gap-1.5 tw-text-[13px] tw-font-semibold tw-text-content";
const TOPIC_ICON = "tw-shrink-0 tw-text-primary";
const TOPIC_DUR =
  "tw-ml-1 tw-rounded-full tw-bg-primary/10 tw-px-[0.4rem] tw-py-[0.05rem] " +
  "tw-text-[0.65rem] tw-font-medium tw-text-content-subtle";
const TOPIC_SUBS = "tw-mt-[0.35rem] tw-flex tw-flex-wrap tw-gap-[0.3rem] tw-pl-[1.125rem]";
const SUBTOPIC =
  "tw-rounded-full tw-border tw-border-solid tw-border-line tw-bg-surface-raised " +
  "tw-px-2 tw-py-[0.1rem] tw-text-[0.7rem] tw-text-content-muted";

// ── Session rows ──
const LIST = "tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-px-3 tw-pb-3.5 tw-pt-2";
// Border colour and background are NOT in the base: `--live` replaces both, and
// two `tw-border-*` utilities on one element resolve by Tailwind's own order.
const ITEM_BASE =
  "tw-grid tw-grid-cols-[40px_1fr_auto] tw-items-start tw-gap-3.5 tw-rounded tw-border " +
  "tw-border-solid tw-p-3.5 tw-transition-[border-color,box-shadow] tw-duration-150 " +
  "max-[768px]:tw-grid-cols-[32px_1fr]";
const ITEM =
  ITEM_BASE + " tw-border-line tw-bg-surface-raised hover:tw-border-line-soft " +
  "hover:tw-shadow-card-hover";
// A live row keeps its live border on hover. The old sheet's `:hover` rule beat
// the `--live` border-colour and turned the one row that matters back to grey.
const ITEM_LIVE =
  ITEM_BASE + " tw-border-danger/45 tw-bg-surface-raised " +
  "tw-bg-[radial-gradient(500px_circle_at_100%_0%,hsl(var(--danger)/0.06),transparent_40%)] " +
  "hover:tw-shadow-card-hover";
const ITEM_RAIL_BASE =
  "tw-inline-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-md " +
  "tw-text-[0.9rem] tw-font-medium";
const ITEM_RAIL =
  ITEM_RAIL_BASE + " tw-text-primary " +
  "tw-bg-[linear-gradient(180deg,hsl(var(--primary)/0.1),hsl(var(--chart-5)/0.06))]";
const ITEM_RAIL_DONE = ITEM_RAIL_BASE + " tw-bg-success/15 tw-text-success";
const ITEM_BODY = "tw-flex tw-min-w-0 tw-flex-col tw-gap-1";
const ITEM_TITLE_ROW = "tw-flex tw-flex-wrap tw-items-center tw-gap-2";
const ITEM_TITLE = "tw-m-0 tw-text-[15px] tw-font-semibold tw-text-content";
const ITEM_DESC = "tw-m-0 tw-text-xs tw-leading-[1.4] tw-text-content-subtle";
const ITEM_META =
  "tw-flex tw-flex-wrap tw-gap-[0.6rem] tw-text-[0.7rem] tw-text-content-subtle";
const ITEM_META_ITEM = "tw-inline-flex tw-items-center tw-gap-1";
const ITEM_ACTIONS =
  "tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2 " +
  "max-[768px]:tw-col-span-full max-[768px]:tw-justify-start";
const ITEM_DONE =
  "tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-success/[0.12] " +
  "tw-px-[0.7rem] tw-py-[0.4rem] tw-text-[13px] tw-font-semibold tw-text-success-fg";
const ITEM_TOGGLE =
  "tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-1 tw-rounded-sm tw-border " +
  "tw-border-solid tw-border-line tw-bg-transparent tw-px-[0.55rem] tw-py-[0.32rem] " +
  "tw-text-xs tw-font-medium tw-text-content-muted " +
  "tw-transition-[border-color,color,background-color] tw-duration-150 " +
  "hover:tw-border-primary hover:tw-text-primary";
const ITEM_TOGGLE_OPEN =
  "tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-1 tw-rounded-sm tw-border " +
  "tw-border-solid tw-border-primary tw-bg-primary/10 tw-px-[0.55rem] tw-py-[0.32rem] " +
  "tw-text-xs tw-font-medium tw-text-primary";

// ── Status pill ──
const STATUS =
  "tw-relative tw-inline-flex tw-items-center tw-gap-[0.3rem] tw-rounded-full " +
  "tw-px-[0.55rem] tw-py-[0.15rem] tw-text-[0.65rem] tw-font-medium tw-uppercase " +
  "tw-tracking-[0.05em]";
const STATUS_TONE = {
  scheduled: "tw-bg-muted tw-text-muted-fg",
  live: "tw-bg-danger tw-pl-[0.95rem] tw-text-on-status",
  completed: "tw-bg-success-soft tw-text-success-fg",
  cancelled: "tw-bg-muted tw-text-muted-fg tw-line-through",
};
const STATUS_PULSE =
  "tw-absolute tw-left-[0.4rem] tw-top-1/2 tw-h-[5px] tw-w-[5px] tw--translate-y-1/2 " +
  "tw-animate-live-pulse tw-rounded-full tw-bg-on-status motion-reduce:tw-animate-none";

// ── Expanded detail panel ──
const DETAIL =
  "tw-col-span-full tw-mt-2 tw-flex tw-flex-col tw-gap-3 tw-border-0 tw-border-t " +
  "tw-border-dashed tw-border-line tw-pt-3.5";
const DETAIL_ERR = "tw-text-[13px] tw-text-danger-fg";
const DETAIL_SEC_TITLE =
  "tw-m-0 tw-flex tw-items-center tw-gap-[0.4rem] tw-text-[0.7rem] tw-font-semibold " +
  "tw-uppercase tw-tracking-[0.05em] tw-text-content-subtle";
const DETAIL_COUNT =
  "tw-inline-flex tw-h-[18px] tw-min-w-[18px] tw-items-center tw-justify-center " +
  "tw-rounded-full tw-bg-primary/10 tw-px-[0.3rem] tw-text-[0.65rem] tw-font-semibold " +
  "tw-text-primary";
const DETAIL_EMPTY = "tw-m-0 tw-text-[13px] tw-text-content-subtle";
const DETAIL_RECORDING =
  "tw-flex tw-flex-col tw-gap-2 tw-rounded-md tw-border tw-border-solid tw-border-line " +
  "tw-bg-surface-alt tw-px-3 tw-py-[0.6rem]";
const DETAIL_UNLOCK =
  "tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-rounded-md " +
  "tw-border tw-border-solid tw-border-line tw-bg-surface-alt tw-px-3 tw-py-2";
const UNLOCK_STATE_BASE =
  "tw-inline-flex tw-items-center tw-gap-[0.4rem] tw-text-[0.78rem] tw-font-medium";
const UNLOCK_STATE = UNLOCK_STATE_BASE + " tw-text-content-subtle";
const UNLOCK_STATE_ON = UNLOCK_STATE_BASE + " tw-text-success";
const UNLOCK_HINT = "tw-font-medium tw-not-italic tw-text-content-subtle tw-opacity-85";

// ── Recordings ──
const REC_LIST = "tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-[0.4rem] tw-p-0";
const REC = "tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3";
const REC_META =
  "tw-inline-flex tw-min-w-0 tw-items-center tw-gap-[0.4rem] tw-text-[13px] tw-text-content-muted";
const REC_META_ICON = "tw-shrink-0 tw-text-primary";
const REC_TITLE = "tw-max-w-[240px] tw-truncate tw-font-medium tw-text-content";
const REC_DATE = "tw-text-[0.7rem] tw-text-content-subtle";
const REC_ACTIONS = "tw-inline-flex tw-flex-wrap tw-items-center tw-gap-[0.4rem]";

// ── Success notice ──
const NOTICE =
  "tw-flex tw-items-center tw-gap-2 tw-rounded tw-border tw-border-solid tw-border-success/25 " +
  "tw-bg-success/10 tw-px-[0.85rem] tw-py-[0.6rem] tw-text-[13px] tw-text-success-fg";
const NOTICE_ICON = "tw-shrink-0";
const NOTICE_X =
  "tw-ml-auto tw-cursor-pointer tw-border-none tw-bg-transparent tw-text-[1.1rem] " +
  "tw-leading-none tw-text-inherit tw-opacity-70 hover:tw-opacity-100";

// ── Trainer add-extra-resource block ──
const ADD = "tw-mt-2 tw-border-0 tw-border-t tw-border-dashed tw-border-line tw-pt-3";
const ADD_TOP =
  "tw-mb-[0.6rem] tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2";
const ADD_SCOPE = "tw-inline-flex tw-rounded-sm tw-bg-surface-alt tw-p-[0.15rem]";
const ADD_SCOPE_BTN =
  "tw-cursor-pointer tw-rounded-[6px] tw-border-none tw-bg-transparent tw-px-[0.6rem] " +
  "tw-py-[0.28rem] tw-text-[0.72rem] tw-font-medium tw-text-content-muted";
const ADD_SCOPE_BTN_ACTIVE =
  "tw-cursor-pointer tw-rounded-[6px] tw-border-none tw-bg-surface-raised tw-px-[0.6rem] " +
  "tw-py-[0.28rem] tw-text-[0.72rem] tw-font-medium tw-text-primary tw-shadow-card";
const ADD_ACTIONS = "tw-flex tw-flex-wrap tw-gap-2";

// ── Resource rows ──
const RES_LIST = "tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0";
const RES =
  "tw-grid tw-grid-cols-[30px_1fr_auto_auto] tw-items-center tw-gap-2.5 tw-rounded-md " +
  "tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-px-[0.7rem] " +
  "tw-py-[0.55rem] max-[768px]:tw-grid-cols-[30px_1fr] max-[768px]:tw-gap-y-[0.4rem]";
const RES_ICON =
  "tw-inline-flex tw-h-[30px] tw-w-[30px] tw-items-center tw-justify-center tw-rounded-sm";
const RES_ICON_TONE = {
  document: "tw-bg-chart-2/[0.12] tw-text-chart-2",
  assignment: "tw-bg-chart-3/[0.12] tw-text-chart-3",
  codingChallenge: "tw-bg-chart-5/[0.12] tw-text-chart-5",
  quiz: "tw-bg-chart-1/[0.12] tw-text-chart-1",
  scorm: "tw-bg-chart-6/[0.12] tw-text-chart-6",
};
const RES_ICON_FALLBACK = "tw-bg-primary/10 tw-text-primary";
const RES_MAIN = "tw-flex tw-min-w-0 tw-flex-col tw-gap-[0.1rem]";
const RES_KIND =
  "tw-text-[0.62rem] tw-font-semibold tw-uppercase tw-tracking-[0.05em] tw-text-content-subtle";
const RES_TITLE = "tw-truncate tw-text-[13px] tw-font-medium tw-text-content";
const RES_STAT =
  "tw-inline-flex tw-items-center tw-gap-[0.3rem] tw-whitespace-nowrap tw-text-[0.72rem] " +
  "tw-font-semibold tw-text-content-muted max-[768px]:tw-col-[2/-1] " +
  "max-[768px]:tw-justify-self-start";
const RES_STAT_MUTED =
  "tw-inline-flex tw-items-center tw-gap-[0.3rem] tw-whitespace-nowrap tw-text-[0.72rem] " +
  "tw-font-medium tw-text-content-subtle max-[768px]:tw-col-[2/-1] " +
  "max-[768px]:tw-justify-self-start";
const RES_STAT_ICON = "tw-text-content-subtle";
const RES_STAT_SUB = "tw-font-medium tw-not-italic tw-text-content-subtle";
const RES_ACTION =
  "tw-inline-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-[0.4rem] " +
  "max-[768px]:tw-col-[2/-1] max-[768px]:tw-justify-self-start";

const RESOURCE_ICON = {
  document: BookOpen,
  assignment: FileText,
  codingChallenge: Code2,
  quiz: HelpCircle,
  scorm: Package,
};

const RESOURCE_LABEL = {
  document: "Document",
  assignment: "Assignment",
  codingChallenge: "Coding Challenge",
  quiz: "Quiz",
  scorm: "SCORM",
};

const formatFileSize = (bytes) => {
  if (!bytes) return "";
  const kb = bytes / 1024;
  return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(1)} MB`;
};

/**
 * ResourceStat — trainer-facing engagement number for one resource. Renders the
 * submission/attempt/solve count against the batch size (e.g. "12/20 submitted"),
 * or the file meta for documents.
 */
function ResourceStat({ r }) {
  const st = r.stats || {};
  if (r.kind === "assignment") {
    return (
      <span className={RES_STAT}>
        <Users size={12} className={RES_STAT_ICON} /> {st.submitted ?? 0}/{st.total ?? 0} submitted
        {st.graded ? <em className={RES_STAT_SUB}>· {st.graded} graded</em> : null}
      </span>
    );
  }
  if (r.kind === "quiz") {
    return (
      <span className={RES_STAT}>
        <Users size={12} className={RES_STAT_ICON} /> {st.attempted ?? 0}/{st.total ?? 0} attempted
        {st.passed ? <em className={RES_STAT_SUB}>· {st.passed} passed</em> : null}
      </span>
    );
  }
  if (r.kind === "codingChallenge") {
    return (
      <span className={RES_STAT}>
        <Users size={12} className={RES_STAT_ICON} /> {st.solved ?? 0}/{st.total ?? 0} solved
      </span>
    );
  }
  if (r.kind === "scorm") {
    return (
      <span className={RES_STAT}>
        <Users size={12} className={RES_STAT_ICON} /> {st.completed ?? 0}/{st.total ?? 0} completed
        {st.attempted ? <em className={RES_STAT_SUB}>· {st.attempted} started</em> : null}
      </span>
    );
  }
  const meta = [r.fileType, formatFileSize(r.fileSize)].filter(Boolean).join(" · ");
  return meta ? <span className={RES_STAT_MUTED}>{meta}</span> : null;
}

/**
 * SessionDetail — expanded panel for one smart session. Shows the captured
 * recording (when available) and every mapped resource (documents, quizzes,
 * assignments, coding challenges) with per-resource engagement stats and a
 * trainer action (Review submissions / Open document).
 */
function SessionDetail({ detail, onOpenDocument, onWatchRecording, onToggleUnlock, onEvaluate, onAddResource, onLaunchScorm }) {
  const session = detail?.session || {};
  const sessionId = session._id;
  const resources = session.resources || [];
  const [scope, setScope] = useState("batch");
  const EVALUABLE = ["assignment", "quiz", "codingChallenge"];

  // Every recording this session produced (kept across restarts) — the trainer
  // can watch each in-app or delete it. Refetched whenever the panel opens.
  const [recordings, setRecordings] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    fetchSessionRecordings(sessionId)
      .then((r) => { if (!cancelled) setRecordings(r); })
      .catch(() => { if (!cancelled) setRecordings([]); });
    return () => { cancelled = true; };
  }, [sessionId]);

  const handleDeleteRecording = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget._id;
    try {
      await deleteSessionRecording(sessionId, id);
      setRecordings((prev) => prev.filter((r) => r._id !== id));
    } catch { /* leave list as-is on failure */ }
    setDeleteTarget(null);
  };

  return (
    <>
      {recordings.length > 0 && (
        <div className={DETAIL_RECORDING}>
          <span className={DETAIL_SEC_TITLE}>
            Recording{recordings.length === 1 ? "" : `s (${recordings.length})`}
          </span>
          <ul className={REC_LIST}>
            {recordings.map((rec) => (
              <li key={rec._id} className={REC}>
                <span className={REC_META}>
                  <PlayCircle size={13} className={REC_META_ICON} />
                  <span className={REC_TITLE}>{rec.title || "Session recording"}</span>
                  {rec.recordedAt && (
                    <span className={REC_DATE}>
                      {new Date(rec.recordedAt).toLocaleDateString("en-IN", {
                        day: "numeric", month: "short", year: "numeric",
                      })}
                    </span>
                  )}
                </span>
                <div className={REC_ACTIONS}>
                  <AppButton
                    variant="outline"
                    size="sm"
                    disabled={!rec.url}
                    onClick={() => onWatchRecording(rec.url, rec.title || session.title)}
                  >
                    <PlayCircle size={14} /> Watch
                  </AppButton>
                  <AppButton variant="outline" size="sm" onClick={() => setDeleteTarget(rec)}>
                    <Trash2 size={14} /> Delete
                  </AppButton>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        onConfirm={handleDeleteRecording}
        title="Delete this recording?"
        message="This permanently removes the recording for this session — students will no longer be able to watch it. This cannot be undone."
        confirmText="Delete recording"
        variant="danger"
      />

      {resources.length > 0 && (
        <div className={DETAIL_UNLOCK}>
          <span className={session.resourcesUnlocked ? UNLOCK_STATE_ON : UNLOCK_STATE}>
            {session.resourcesUnlocked ? (
              <><Unlock size={13} className="tw-shrink-0" /> Resources are visible to students</>
            ) : (
              <><Lock size={13} className="tw-shrink-0" /> Resources are hidden from students</>
            )}
            {session.unlockMode === "manual" && !session.resourcesUnlocked && (
              <em className={UNLOCK_HINT}>— manual unlock</em>
            )}
          </span>
          <AppButton
            variant="outline"
            size="sm"
            onClick={() => onToggleUnlock(sessionId, !session.resourcesUnlocked)}
          >
            {session.resourcesUnlocked ? (
              <><Lock size={14} /> Lock</>
            ) : (
              <><Unlock size={14} /> Unlock for students</>
            )}
          </AppButton>
        </div>
      )}

      <div className={DETAIL_SEC_TITLE}>
        Session resources
        {resources.length > 0 && (
          <span className={DETAIL_COUNT}>{resources.length}</span>
        )}
      </div>

      {resources.length === 0 ? (
        <p className={DETAIL_EMPTY}>
          No documents, quizzes or assignments are attached to this session.
        </p>
      ) : (
        <ul className={RES_LIST}>
          {resources.map((r, i) => {
            const Icon = RESOURCE_ICON[r.kind] || FileText;
            return (
              <li key={`${r.kind}-${r.refId}-${i}`} className={RES}>
                <span className={`${RES_ICON} ${RES_ICON_TONE[r.kind] || RES_ICON_FALLBACK}`}>
                  <Icon size={15} />
                </span>
                <div className={RES_MAIN}>
                  <span className={RES_KIND}>{RESOURCE_LABEL[r.kind] || "Resource"}</span>
                  <span className={RES_TITLE}>{r.title || "Untitled"}</span>
                </div>
                <ResourceStat r={r} />
                <div className={RES_ACTION}>
                  {EVALUABLE.includes(r.kind) ? (
                    <AppButton variant="outline" size="sm" onClick={() => onEvaluate(r)}>
                      <ClipboardCheck size={14} /> Evaluate
                    </AppButton>
                  ) : r.kind === "scorm" ? (
                    <>
                      <AppButton variant="outline" size="sm" onClick={() => onLaunchScorm(r.refId)}>
                        <PlayCircle size={14} /> Open
                      </AppButton>
                      <AppButton variant="outline" size="sm" onClick={() => onEvaluate(r)}>
                        <Users size={14} /> Progress
                      </AppButton>
                    </>
                  ) : r.kind === "document" ? (
                    <AppButton variant="outline" size="sm" onClick={() => onOpenDocument(r)}>
                      <Download size={14} /> Open
                    </AppButton>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {session.allowTrainerAdditions && (
        <div className={ADD}>
          <div className={ADD_TOP}>
            <span className={DETAIL_SEC_TITLE}>Add extra resource</span>
            <div className={ADD_SCOPE} role="group" aria-label="Apply to">
              <button
                type="button"
                className={scope === "batch" ? ADD_SCOPE_BTN_ACTIVE : ADD_SCOPE_BTN}
                onClick={() => setScope("batch")}
              >
                This batch
              </button>
              <button
                type="button"
                className={scope === "all" ? ADD_SCOPE_BTN_ACTIVE : ADD_SCOPE_BTN}
                onClick={() => setScope("all")}
              >
                All upcoming batches
              </button>
            </div>
          </div>
          <div className={ADD_ACTIONS}>
            <AppButton variant="outline" size="sm" onClick={() => onAddResource({ kind: "document", scope })}>
              <BookOpen size={14} /> Add material
            </AppButton>
            <AppButton variant="outline" size="sm" onClick={() => onAddResource({ kind: "assignment", scope })}>
              <Plus size={14} /> Add assignment
            </AppButton>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * TrainerSmartSessions
 *
 * Lists all smart sessions for a batch's course, grouped by module, with
 * Start Class / End Session controls. Reuses the admin list endpoint
 * (`adminListSessions`) — same payload, different audience: trainers see
 * lifecycle controls instead of authoring controls.
 *
 * Starting a session is delegated to the parent (`onStartSession`) so it runs
 * the same system-check + Zoom flow as the normal "Start Class".
 * Modules with no authored session expose `onStartLiveClass` to start a plain
 * live class instead.
 *
 * Dates come from the batch's class plan (GET /syllabus/batch/:id/plan) — the
 * same plan, with the same reschedule, as a day-wise or module-wise batch.
 * `canReschedule` shows the controls (default true; the server decides).
 */
const TrainerSmartSessions = ({ courseId, batchId, onStartSession, onStartLiveClass, canReschedule = true }) => {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [modules, setModules] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [acting, setActing] = useState(null);            // session id currently mid-action
  const [endTarget, setEndTarget] = useState(null);       // session to end
  const [restartTarget, setRestartTarget] = useState(null); // completed session to re-run

  // ── Per-session expandable detail (resources + submission stats) ──
  const [expanded, setExpanded] = useState(() => new Set());  // open session ids
  const [details, setDetails] = useState(() => new Map());     // sessionId → { session, stats }
  const [detailLoading, setDetailLoading] = useState(() => new Set());
  const [detailErr, setDetailErr] = useState(() => new Map()); // sessionId → message

  // Evaluation drawer + trainer add-resource picker
  const [evalTarget, setEvalTarget] = useState(null); // { sessionId, resource }
  const [addState, setAddState] = useState(null);      // { sessionId, kind, scope }
  const [scormPreview, setScormPreview] = useState(null); // refId of SCORM lesson to play
  const [recording, setRecording] = useState(null);      // { url, title } — in-app recording viewer
  const [notice, setNotice] = useState("");
  const [plan, setPlan] = useState(null);                // the batch's dated plan, when it is a Smart Learning one
  const [reschedule, setReschedule] = useState(null);    // { session: plan row, date }

  // In-app document viewer (PDF/PPT/DOCX/image/text rendered inline) — replaces
  // the old window.open(r2SignedUrl) that navigated the trainer off to the raw file.
  const { openMaterial, viewer } = useMaterialViewer();

  const load = useCallback(
    async (silent = false) => {
      if (!courseId) return;
      if (!silent) setLoading(true);
      setErr("");
      try {
        // The plan is a second read; the list must not fail with it.
        const [data, planData] = await Promise.all([
          adminListSessions({ courseId, batchId }),
          batchId
            ? apiClient.get(`/syllabus/batch/${batchId}/plan`).then((res) => res.data).catch(() => null)
            : null,
        ]);
        setModules(data.modules || []);
        setSessions(data.sessions || []);
        setPlan(planData?.source === "smart" ? planData : null);
      } catch (e) {
        setErr(e?.response?.data?.message || "Failed to load sessions.");
      } finally {
        setLoading(false);
      }
    },
    [courseId, batchId],
  );

  useEffect(() => {
    load();
  }, [load]);

  // Fetch (or refresh) a session's trainer detail — resources hydrated with
  // titles, deep links, and per-resource submission/attempt stats.
  const loadDetail = useCallback(
    async (sessionId) => {
      setDetailLoading((prev) => new Set(prev).add(sessionId));
      setDetailErr((prev) => {
        const next = new Map(prev);
        next.delete(sessionId);
        return next;
      });
      try {
        const data = await fetchTrainerSessionDetail(sessionId, batchId);
        setDetails((prev) => new Map(prev).set(sessionId, data));
      } catch (e) {
        setDetailErr((prev) =>
          new Map(prev).set(sessionId, e?.response?.data?.message || "Failed to load details."),
        );
      } finally {
        setDetailLoading((prev) => {
          const next = new Set(prev);
          next.delete(sessionId);
          return next;
        });
      }
    },
    [batchId],
  );

  const toggleExpand = useCallback(
    (sessionId) => {
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(sessionId)) {
          next.delete(sessionId);
        } else {
          next.add(sessionId);
          loadDetail(sessionId); // refetch on open so stats are always current
        }
        return next;
      });
    },
    [loadDetail],
  );

  // Open a session document in the in-app viewer (no more raw R2 tab).
  const openDocument = useCallback(
    (r) => {
      if (!r?.refId) return;
      openMaterial({ _id: r.refId, title: r.title, fileType: r.fileType });
    },
    [openMaterial],
  );

  // Trainer picked/created a document or assignment to attach to a session.
  const handlePickedResource = useCallback(
    async (picked) => {
      const ctx = addState;
      setAddState(null);
      if (!ctx || !picked?._id) return;
      try {
        await addTrainerSessionResource(ctx.sessionId, {
          kind: picked.kind,
          refId: picked._id,
          title: picked.title,
          scope: ctx.scope,
          batchId,
        });
        setNotice(
          ctx.scope === "all"
            ? "Added for all upcoming batches — current batches keep their own copy."
            : "Added to this batch.",
        );
        await load(true);
        loadDetail(ctx.sessionId);
      } catch (e) {
        setErr(e?.response?.data?.message || "Failed to add resource.");
      }
    },
    [addState, batchId, load, loadDetail],
  );

  const grouped = useMemo(() => {
    const byNum = new Map(
      modules.map((m) => [
        m.moduleNumber,
        { moduleNumber: m.moduleNumber, title: m.title, topics: m.topics || [], sessions: [] },
      ]),
    );
    for (const s of sessions) {
      if (!byNum.has(s.moduleNumber)) {
        byNum.set(s.moduleNumber, {
          moduleNumber: s.moduleNumber,
          title: s.moduleTitle || `Module ${s.moduleNumber}`,
          topics: [],
          sessions: [],
        });
      }
      byNum.get(s.moduleNumber).sessions.push(s);
    }
    for (const mod of byNum.values()) {
      mod.sessions.sort((a, b) => a.order - b.order);
    }
    return Array.from(byNum.values()).sort((a, b) => a.moduleNumber - b.moduleNumber);
  }, [modules, sessions]);

  const handleConfirmEnd = async () => {
    if (!endTarget) return;
    const endedId = endTarget._id;
    setActing(endedId);
    try {
      await endSmartSession(endedId);
      await load(true);
      // If the trainer has this session expanded, refresh its now-unlocked
      // resources and updated stats.
      if (expanded.has(endedId)) loadDetail(endedId);
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to end session.");
    } finally {
      setActing(null);
      setEndTarget(null);
    }
  };

  const handleToggleUnlock = useCallback(
    async (sessionId, unlocked) => {
      try {
        await setSessionResourcesUnlock(sessionId, unlocked);
        setNotice(unlocked ? "Resources unlocked for students." : "Resources hidden from students.");
        await load(true);
        loadDetail(sessionId);
      } catch (e) {
        setErr(e?.response?.data?.message || "Failed to update resource visibility.");
      }
    },
    [load, loadDetail],
  );

  const handleConfirmRestart = async () => {
    if (!restartTarget) return;
    const id = restartTarget._id;
    setActing(id);
    try {
      await restartSmartSession(id);
      await load(true);
      if (expanded.has(id)) loadDetail(id);
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to restart session.");
    } finally {
      setActing(null);
      setRestartTarget(null);
    }
  };

  // Each session's row in the dated plan, by session id.
  const planRows = useMemo(() => {
    const map = new Map();
    for (const row of plan?.sessions || []) {
      if (row.smart?.sessionId) map.set(String(row.smart.sessionId), row);
    }
    return map;
  }, [plan]);

  // A Shimmer in the shape of the module list (Phase 6.5) — three module cards,
  // which is what a typical course shows before scrolling.
  if (loading) {
    return (
      <div className={ROOT}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Shimmer key={i} height={148} />
        ))}
      </div>
    );
  }

  if (err && !sessions.length) {
    return <div className={ERROR}>{err}</div>;
  }

  if (grouped.length === 0) {
    return (
      <div className={EMPTY}>
        <div className={EMPTY_ICON}><Sparkles size={22} /></div>
        <h4 className={EMPTY_TITLE}>No modules yet</h4>
        <p className={EMPTY_DESC}>
          Ask the course admin to set up a module-wise syllabus in
          <strong> Courses → Syllabus</strong>. Modules and smart sessions will
          appear here for you to start when class begins.
        </p>
      </div>
    );
  }

  return (
    <div className={ROOT}>
      {err && (
        <div className={ERROR_INLINE}>{err}</div>
      )}
      {notice && (
        <div className={NOTICE} role="status">
          <CheckCircle2 size={14} className={NOTICE_ICON} />
          <span>{notice}</span>
          <button type="button" className={NOTICE_X} onClick={() => setNotice("")} aria-label="Dismiss">×</button>
        </div>
      )}
      {scheduleNeedsNotice(plan?.schedule) && (
        <ScheduleNotice
          schedule={plan.schedule}
          sessions={plan.sessions}
          onRepair={canReschedule ? (session, date) => setReschedule({ session, date }) : undefined}
        />
      )}

      {grouped.map((mod) => (
        <section key={mod.moduleNumber} className={MODULE}>
          <header className={MODULE_HEAD}>
            <span className={MODULE_ICON}><Layers size={14} /></span>
            <h5 className={MODULE_TITLE}>
              Module {mod.moduleNumber}: {mod.title}
            </h5>
            <span className={MODULE_COUNT}>
              {mod.sessions.length} session{mod.sessions.length === 1 ? "" : "s"}
            </span>
          </header>

          {mod.topics?.length > 0 && (
            <div className={TOPICS}>
              {mod.topics.map((t, ti) => (
                <div key={ti}>
                  <div className={TOPIC_TITLE}>
                    <BookOpen size={12} className={TOPIC_ICON} />
                    <span>{t.title}</span>
                    {t.estimatedDuration ? (
                      <span className={TOPIC_DUR}>{t.estimatedDuration}m</span>
                    ) : null}
                  </div>
                  {Array.isArray(t.subtopics) && t.subtopics.length > 0 && (
                    <div className={TOPIC_SUBS}>
                      {t.subtopics.map((st, si) => (
                        <span key={si} className={SUBTOPIC}>{st}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {mod.sessions.length === 0 ? (
            <div className={MODULE_EMPTY}>
              <span>No smart session available for this module.</span>
              <AppButton variant="outline" size="sm" onClick={() => onStartLiveClass?.()}>
                <Radio size={14} />
                Start Live Class
              </AppButton>
            </div>
          ) : (
            <ul className={LIST}>
              {mod.sessions.map((s) => {
                const isLive = s.status === "live";
                const isCompleted = s.status === "completed";
                const isBusy = acting === s._id;
                const counts = (s.resources || []).reduce((acc, r) => {
                  acc[r.kind] = (acc[r.kind] || 0) + 1;
                  return acc;
                }, {});
                const isExpanded = expanded.has(s._id);
                const detail = details.get(s._id);
                const isDetailLoading = detailLoading.has(s._id);
                const dErr = detailErr.get(s._id);
                const planRow = planRows.get(String(s._id));

                return (
                  <li key={s._id} className={isLive ? ITEM_LIVE : ITEM}>
                    <div className={isCompleted ? ITEM_RAIL_DONE : ITEM_RAIL}>{s.order}</div>
                    <div className={ITEM_BODY}>
                      <div className={ITEM_TITLE_ROW}>
                        <h6 className={ITEM_TITLE}>{s.title}</h6>
                        <span className={`${STATUS} ${STATUS_TONE[s.status] || STATUS_TONE.scheduled}`}>
                          {isLive && <span className={STATUS_PULSE} />}
                          {s.status}
                        </span>
                      </div>
                      {planRow?.plannedDate ? (
                        <SessionDateLine session={planRow} />
                      ) : planRow && !isCompleted ? (
                        <span className="tw-mt-0.5 tw-block tw-text-xs tw-font-medium tw-text-warning-fg">No date yet</span>
                      ) : null}
                      {s.description && (
                        <p className={ITEM_DESC}>{s.description}</p>
                      )}
                      <div className={ITEM_META}>
                        {s.plannedDurationMinutes ? (
                          <span className={ITEM_META_ITEM}>
                            <Clock size={11} /> {s.plannedDurationMinutes} min
                          </span>
                        ) : null}
                        {Object.entries(counts).map(([k, n]) => {
                          const Icon = RESOURCE_ICON[k] || FileText;
                          return (
                            <span key={k} className={ITEM_META_ITEM}>
                              <Icon size={11} /> {n}
                            </span>
                          );
                        })}
                      </div>
                    </div>

                    <div className={ITEM_ACTIONS}>
                      {isLive ? (
                        <>
                          {s.meetingJoinUrl && (
                            <AppButton
                              variant="outline"
                              size="sm"
                              as="a"
                              href={s.meetingJoinUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <ExternalLink size={14} />
                              Open meeting
                            </AppButton>
                          )}
                          <AppButton
                            variant="default"
                            size="sm"
                            onClick={() => setEndTarget(s)}
                            disabled={isBusy}
                          >
                            {isBusy ? <Spinner animation="border" size="sm" /> : <Square size={14} />}
                            End Session
                          </AppButton>
                        </>
                      ) : isCompleted ? (
                        <>
                          <span className={ITEM_DONE}>
                            <CheckCircle2 size={14} /> Completed
                          </span>
                          <AppButton
                            variant="outline"
                            size="sm"
                            onClick={() => setRestartTarget(s)}
                            disabled={isBusy}
                          >
                            {isBusy ? <Spinner animation="border" size="sm" /> : <RotateCcw size={14} />}
                            Restart
                          </AppButton>
                        </>
                      ) : (
                        <>
                          <AppButton
                            variant="default"
                            size="sm"
                            onClick={() => onStartSession?.(s)}
                            disabled={isBusy}
                          >
                            <Radio size={14} />
                            Start Class
                          </AppButton>
                          {canReschedule && planRow && (
                            <AppButton
                              variant="outline"
                              size="sm"
                              onClick={() => setReschedule({ session: planRow, date: "" })}
                              disabled={isBusy}
                            >
                              <CalendarClock size={14} />
                              Reschedule
                            </AppButton>
                          )}
                        </>
                      )}
                      <button
                        type="button"
                        className={isExpanded ? ITEM_TOGGLE_OPEN : ITEM_TOGGLE}
                        onClick={() => toggleExpand(s._id)}
                        aria-expanded={isExpanded}
                      >
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        Details
                      </button>
                    </div>

                    {isExpanded && (
                      <div className={DETAIL}>
                        {isDetailLoading ? (
                          <Shimmer height={96} />
                        ) : dErr ? (
                          <div className={DETAIL_ERR}>{dErr}</div>
                        ) : detail ? (
                          <SessionDetail
                            detail={detail}
                            onOpenDocument={openDocument}
                            onWatchRecording={(url, title) => setRecording({ url, title })}
                            onToggleUnlock={handleToggleUnlock}
                            onEvaluate={(resource) => setEvalTarget({ sessionId: s._id, resource })}
                            onAddResource={({ kind, scope }) => setAddState({ sessionId: s._id, kind, scope })}
                            onLaunchScorm={(refId) => setScormPreview(refId)}
                          />
                        ) : null}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      ))}

      <ConfirmModal
        show={!!endTarget}
        onHide={() => setEndTarget(null)}
        onConfirm={handleConfirmEnd}
        title="End this session?"
        message={
          endTarget
            ? `"${endTarget.title}" will be marked completed and its mapped resources (assignments, coding challenges, quizzes, documents) will unlock for all enrolled students.`
            : ""
        }
        confirmText="End Session"
        variant="warning"
      />

      <ConfirmModal
        show={!!restartTarget}
        onHide={() => setRestartTarget(null)}
        onConfirm={handleConfirmRestart}
        title="Restart this session?"
        message={
          restartTarget
            ? `"${restartTarget.title}" will move back to Scheduled and its resources (assignments, quizzes, documents) will be RE-LOCKED for students. Every student's progress for this session — attendance, quiz, assignment and SCORM completion — will be cleared so you can run it again. Recordings are kept; delete them from the session details if you no longer need them.`
            : ""
        }
        confirmText="Restart Session"
        variant="warning"
      />

      <RescheduleDialog
        show={!!reschedule}
        onHide={() => setReschedule(null)}
        batchId={batchId}
        session={reschedule?.session}
        initialDate={reschedule?.date || ""}
        today={plan?.schedule?.today}
        onSaved={(message) => {
          setNotice(message);
          load(true);
        }}
      />

      <SessionEvaluationModal
        show={!!evalTarget}
        onHide={() => setEvalTarget(null)}
        sessionId={evalTarget?.sessionId}
        batchId={batchId}
        resource={evalTarget?.resource}
      />

      {/* Trainer add-resource picker (document / assignment) */}
      <AppModal
        show={!!addState}
        onHide={() => setAddState(null)}
        size="lg"
        title={addState ? `Add ${addState.kind === "assignment" ? "an assignment" : `a ${(KIND_META[addState.kind]?.label || "resource").toLowerCase()}`}` : ""}
      >
        {addState && (
          <ResourcePicker
            key={`${addState.kind}-${addState.sessionId}`}
            kind={addState.kind}
            courseId={courseId}
            batchId={batchId}
            onPick={handlePickedResource}
            onClose={() => setAddState(null)}
            initialTab="create"
            allowScorm={false}
            showHeader={false}
          />
        )}
      </AppModal>

      {/* SCORM player — trainer opens a session's SCORM package in-app. */}
      <AppModal
        show={!!scormPreview}
        onHide={() => setScormPreview(null)}
        title="SCORM content"
        icon={<Package size={18} />}
        size="xl"
        closeOnOverlay={false}
      >
        {scormPreview && (
          <div className="tw-h-[74vh]">
            <ScormPlayer lessonId={scormPreview} />
          </div>
        )}
      </AppModal>

      {/* Session recording — played in-app instead of opening the raw R2 URL. */}
      <AppModal
        show={!!recording}
        onHide={() => setRecording(null)}
        title={recording?.title ? `Recording · ${recording.title}` : "Session recording"}
        icon={<PlayCircle size={18} />}
        size="xl"
        closeOnOverlay={false}
      >
        {recording?.url && (
          // --media-mat, not "#000": the letterbox behind a video is a token, and
          // this was one of the 139 inline colour literals the lint counts.
          <video
            src={recording.url}
            controls
            autoPlay
            controlsList="nodownload"
            onContextMenu={(e) => e.preventDefault()}
            className="tw-max-h-[74vh] tw-w-full tw-rounded-sm tw-bg-[var(--media-mat)]"
          >
            Your browser does not support the video tag.
          </video>
        )}
      </AppModal>

      {/* In-app document viewer (rendered once; null until a doc is opened). */}
      {viewer}
    </div>
  );
};

export default TrainerSmartSessions;
